import { Handle, Position, useStoreApi, useUpdateNodeInternals } from '@xyflow/react';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import {
  type ProcessStepRecording,
  type WhoElseDoesThisItem,
  type PopulatedUser,
  type System,
  type Artifact
} from '@bliks-io/contracts';
import { getEntityRefId } from '@/lib/entityRefUtils';
import { assigneeFieldVisibility } from '@/lib/assigneeFieldVisibility';
import WhoElseDoesThisCard from '../components/cards/WhoElseDoesThisCard';
import StepCardNode from './StepCardNode';

interface StepWithWhoElseNodeProps {
  id: string;
  data: {
    // Step card data
    recording: ProcessStepRecording | null;
    availableUsers: PopulatedUser[];
    availableSystems: System[];
    availableArtifacts: Artifact[];
    onChange: (field: string, value: unknown) => Promise<void>;
    onChangeDebounced: (field: string, value: unknown) => void;
    onFlushPendingChanges: () => Promise<void>;
    onAddInput: () => void;
    onEditInput: (index: number) => void;
    onRemoveInput: (index: number) => void;
    onOpenDecisionTable: () => void;
    // Who else does this data
    whoElseValue: WhoElseDoesThisItem[];
    onWhoElseChange: (value: WhoElseDoesThisItem[]) => Promise<void>;
  };
}

// Shown for one paint before the layout effect measures the real anchor — kept
// roughly aligned with current section heights so the first-frame handle isn't
// wildly off; real position takes over once ResizeObserver fires.
const FALLBACK_TASK_TOP = 550;
const FALLBACK_DECISION_TOP = 350;

// Sub-pixel jitter from getBoundingClientRect() routinely produces no-op
// re-renders; ignore changes finer than this when deciding to commit.
const MIN_TOP_DELTA_PX = 0.5;

function StepWithWhoElseNode({ id, data }: StepWithWhoElseNodeProps) {
  const isDecision = data.recording?.actionType === 'decision';
  const containerRef = useRef<HTMLDivElement>(null);
  const sourceAnchorRef = useRef<HTMLDivElement>(null);
  const initialTop = isDecision ? FALLBACK_DECISION_TOP : FALLBACK_TASK_TOP;
  const [sourceTop, setSourceTop] = useState<number>(initialTop);
  const lastSourceTopRef = useRef<number>(initialTop);
  const storeApi = useStoreApi();
  const updateNodeInternals = useUpdateNodeInternals();

  // Measure the y-center of the active anchor section relative to the container
  // so the source handle stays aligned as inner heights change (e.g., helpers
  // list growing/shrinking with selections). Zoom is read on-demand via the
  // store API rather than subscribed in render, so pan/zoom doesn't re-render
  // the node or tear down the observer.
  useLayoutEffect(
    function measureSourceHandleAnchor() {
      const anchor = sourceAnchorRef.current;
      const container = containerRef.current;
      if (!anchor || !container) return;

      const measure = () => {
        const z = storeApi.getState().transform[2] || 1;
        const anchorRect = anchor.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = (anchorRect.top - containerRect.top) / z;
        const height = anchorRect.height / z;
        const next = relativeTop + height / 2;
        if (Math.abs(lastSourceTopRef.current - next) < MIN_TOP_DELTA_PX) return;
        lastSourceTopRef.current = next;
        setSourceTop(next);
        updateNodeInternals(id);
      };

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(anchor);
      observer.observe(container);
      return () => observer.disconnect();
    },
    [isDecision, id, updateNodeInternals, storeApi]
  );

  const assignedToModel = data.recording?.assignedToModel;
  const showWhoElse = assigneeFieldVisibility(assignedToModel).whoElse;
  const assigneeUserId =
    assignedToModel === 'User' ? getEntityRefId(data.recording?.assignedTo) : undefined;

  return (
    <div className="flex flex-col items-center">
      {/* Step card with its connection handles */}
      <div className="relative" ref={containerRef}>
        <Handle
          id="target"
          type="target"
          position={Position.Left}
          style={{
            top: '130px',
            left: '5px',
            background: 'transparent',
            border: 'none',
            width: '10px',
            height: '10px'
          }}
        />
        <StepCardNode
          data={{
            recording: data.recording,
            availableUsers: data.availableUsers,
            availableSystems: data.availableSystems,
            availableArtifacts: data.availableArtifacts,
            onChange: data.onChange,
            onChangeDebounced: data.onChangeDebounced,
            onFlushPendingChanges: data.onFlushPendingChanges,
            onAddInput: data.onAddInput,
            onEditInput: data.onEditInput,
            onRemoveInput: data.onRemoveInput,
            onOpenDecisionTable: data.onOpenDecisionTable
          }}
          sourceAnchorRef={sourceAnchorRef}
        />
        <Handle
          id="source"
          type="source"
          position={Position.Right}
          style={{
            top: `${sourceTop}px`,
            right: '5px',
            background: 'transparent',
            border: 'none',
            width: '10px',
            height: '10px'
          }}
        />
      </div>

      {/* Connecting line + Who Else Does This — hidden for System and ExternalContact
          assignees (alternative-performer concept doesn't apply to non-user actors) */}
      {showWhoElse && (
        <>
          <div className="self-stretch h-[50px] flex justify-center cursor-grab active:cursor-grabbing">
            <div className="w-0.5 h-full" style={{ backgroundColor: '#d1d5db' }} />
          </div>

          <WhoElseDoesThisCard
            value={data.whoElseValue}
            onChange={data.onWhoElseChange}
            assigneeUserId={assigneeUserId}
            processId={data.recording?.process}
            recordingId={data.recording?._id}
          />
        </>
      )}
    </div>
  );
}

export default memo(StepWithWhoElseNode);
